import { ReactNode, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'

import { useMultiApprovalScope } from '../hooks/useMultiApprovalScope'

import { Badge, ButtonText, CardDashboard, Flex, ProgressBar, Text, theme } from '@percent/lemonade'
import {
  DisbursementBatchProgress,
  DisbursementBatchProgressBucket,
  DisbursementBatchProgressDisbursementBuckets
} from '@percent/admin-dashboard/api/actions/disbursements/disbursements.types'
import { useCurrencies, usePermissions } from '@percent/admin-dashboard/common/hooks'
import {
  usePostHogFeatureFlag,
  POSTHOG_FEATURE_FLAGS
} from '@percent/admin-dashboard/common/hooks/usePostHogFeatureFlag'
import { useServices } from '@percent/admin-dashboard/containers/service/ServiceContext'
import { formatAmountFromMinorUnits } from '@percent/admin-dashboard/common/utility/formatAmount/formatAmount'
import { useQuery } from '@percent/utility'
import { PaymentInProgressProps } from './PaymentInProgress.types'
import style from './PaymentInProgress.module.scss'

type MetricCellProps = {
  label: string
  value: string
  topRight?: ReactNode
  bottomRight?: ReactNode
  footer?: ReactNode
  tinted?: boolean
}

function MetricCell({ label, value, topRight, bottomRight, footer, tinted = false }: Readonly<MetricCellProps>) {
  return (
    <Flex
      direction="column"
      className={style.cell}
      style={{
        backgroundColor: tinted ? theme.scale.error50 : theme.scale.white,
        borderColor: tinted ? theme.scale.error100 : theme.scale.neutral200
      }}
    >
      <div className={style.cellTopRow}>
        <Text size="small" color={theme.scale.neutral900} fontWeight="medium">
          {label}
        </Text>
        {topRight}
      </div>
      <div className={style.amountRow}>
        <Text size="small" color={theme.scale.neutral700} fontWeight="medium">
          {value}
        </Text>
        {bottomRight}
      </div>
      {footer}
    </Flex>
  )
}

const sumBuckets = (buckets: DisbursementBatchProgressBucket[]): DisbursementBatchProgressBucket =>
  buckets.reduce<DisbursementBatchProgressBucket>(
    (acc, bucket) => ({ count: acc.count + bucket.count, amount: acc.amount + bucket.amount }),
    { count: 0, amount: 0 }
  )

const PENDING_PAYMENT_KEYS: (keyof DisbursementBatchProgressDisbursementBuckets)[] = [
  'approved',
  'payment_initiated',
  'in_transit'
]

const SETTLED_PAYMENT_KEYS: (keyof DisbursementBatchProgressDisbursementBuckets)[] = [
  'paid',
  'failed',
  'cancelled',
  'returned'
]

const PROGRESS_POLLING_INTERVAL_MS = 5_000

export function PaymentInProgress({
  disbursementBatchId,
  counts,
  foundationId,
  detailedCounts,
  onFilterFailedPayments,
  onProgressChange
}: Readonly<PaymentInProgressProps>) {
  const { t } = useTranslation()
  const { disbursementsService } = useServices()
  const { userHasPermissions } = usePermissions()
  const { currencyInfo } = useCurrencies()
  const internalFgaDoraDisbursementsFeatureFlag = usePostHogFeatureFlag(
    POSTHOG_FEATURE_FLAGS.INTERNAL_FGA_DORA_DISBURSEMENTS
  )
  const { isInScope: isInMultiApprovalScope } = useMultiApprovalScope(foundationId, detailedCounts)

  const postManagerApprovalCount =
    counts.approved + counts.paymentInitiated + counts.inTransit + counts.paid + counts.failed + counts.returned
  const isPostManagerApproval = postManagerApprovalCount > 0
  const canView = !internalFgaDoraDisbursementsFeatureFlag || userHasPermissions('disbursements:can_view')
  const shouldRender = isInMultiApprovalScope && isPostManagerApproval && canView

  const { data: progress } = useQuery(
    ['disbursement-batch-progress', disbursementBatchId],
    disbursementsService.getDisbursementBatchProgress,
    { id: disbursementBatchId },
    {
      enabled: shouldRender,
      refetchInterval: query => {
        if (!shouldRender) return false

        const data = query.state.data as DisbursementBatchProgress | undefined
        const allPaymentsSettled = !!data && PENDING_PAYMENT_KEYS.every(key => data.disbursements[key].count === 0)

        return allPaymentsSettled ? false : PROGRESS_POLLING_INTERVAL_MS
      }
    }
  )

  // Payments settle server-side, without any action on this page, so tell the parent
  // to refetch the batch whenever polling shows a disbursement changing bucket.
  const progressSignature = progress
    ? Object.entries(progress.disbursements)
        .map(([key, bucket]) => `${key}:${bucket.count}:${bucket.amount}`)
        .sort((a, b) => a.localeCompare(b))
        .join('|')
    : null
  const onProgressChangeRef = useRef(onProgressChange)
  const previousProgressSignature = useRef<string | null>(null)

  onProgressChangeRef.current = onProgressChange

  useEffect(() => {
    if (progressSignature === null) return

    const isFirstSignature = previousProgressSignature.current === null

    if (!isFirstSignature && previousProgressSignature.current !== progressSignature) {
      onProgressChangeRef.current?.()
    }

    previousProgressSignature.current = progressSignature
  }, [progressSignature])

  if (!shouldRender || !progress) {
    return null
  }

  const { currencyCode, disbursements } = progress
  const { paid, failed } = disbursements
  const total = sumBuckets(Object.values(disbursements))
  const pendingPayment = sumBuckets(PENDING_PAYMENT_KEYS.map(key => disbursements[key]))
  const settled = sumBuckets(SETTLED_PAYMENT_KEYS.map(key => disbursements[key]))

  const progressPercentage = total.count === 0 ? 0 : Math.floor((settled.count / total.count) * 100)
  const hasUnresolvedFailures = failed.count > 0 && progressPercentage === 100

  const formatAmount = (value: number) => formatAmountFromMinorUnits({ value, currencyCode, currencies: currencyInfo })

  return (
    <CardDashboard title={t('paymentInProgress.title')}>
      <Flex direction="column" gap={8}>
        <div className={style.row}>
          <MetricCell
            label={t('paymentInProgress.progress')}
            value={`${progressPercentage}%`}
            footer={
              <div
                role="progressbar"
                aria-valuenow={progressPercentage}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-label={t('paymentInProgress.progressLabel')}
                className={style.progressBarWrapper}
              >
                <ProgressBar
                  progressPercentage={progressPercentage}
                  backgroundColorComplete="purple400"
                  backgroundColorIncomplete="purple400"
                  sizeVariant="x-small"
                />
              </div>
            }
          />

          <MetricCell
            label={t('paymentInProgress.total')}
            value={formatAmount(total.amount)}
            topRight={
              <Badge variant="default" icon="claims">
                {String(total.count)}
              </Badge>
            }
          />
          <MetricCell
            label={t('paymentInProgress.pendingPayment')}
            value={formatAmount(pendingPayment.amount)}
            topRight={
              <Badge variant="default" icon="clock">
                {String(pendingPayment.count)}
              </Badge>
            }
          />
          <MetricCell
            label={t('paymentInProgress.paid')}
            value={formatAmount(paid.amount)}
            topRight={
              <Badge variant="positive" icon="approve">
                {String(paid.count)}
              </Badge>
            }
          />
          <MetricCell
            label={t('paymentInProgress.failed')}
            value={formatAmount(failed.amount)}
            topRight={
              <Badge variant="critical" icon="reject">
                {String(failed.count)}
              </Badge>
            }
            bottomRight={
              hasUnresolvedFailures ? (
                <ButtonText onClick={onFilterFailedPayments}>{t('paymentInProgress.filterPayments')}</ButtonText>
              ) : null
            }
            tinted={hasUnresolvedFailures}
          />
        </div>
      </Flex>
    </CardDashboard>
  )
}
